/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 블록 기본클래스를 정의한다.
 *
 * @file /src/rootin/base/Block.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 9.
 */
import { Element, RootinElement } from '@rootin/base/Element';
import { Container } from '@rootin/elements/container/Container';
import { Anchor } from '@rootin/elements/anchor/Anchor';
import { Menu } from '@rootin/core/Menu';
import { Editor } from '@rootin/core/Editor';
import { BlockClass } from '@rootin/base/Blocks';
import { Dom } from '@utils/Dom';
import { LanguageText } from '@utils/Language';
import { UUID } from '@utils/UUID';
import { Html } from '@utils/Html';

export abstract class Block {
    /**
     * 블록고유값
     */
    #id: string = null;

    /**
     * 블록타입
     */
    static type: string;

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic';

    /**
     * 블록아이콘
     */
    static icon: string;

    /**
     * 블록명
     */
    static label: LanguageText;

    /**
     * 블록기본값
     */
    defaultValue: { [key: string]: any } | null = null;

    /**
     * 블록 DOM
     */
    $block: Dom = null;

    /**
     * 앵커 엘리먼트 (블록전체 캐럿 정지점)
     */
    anchor: Anchor = null;

    /**
     * 이웃 블록으로부터 블록 전체 포커스 상태로 진입했을 때, 어느 방향에서 들어왔는지 기억한다.
     */
    anchoredFrom: 'before' | 'after' | null = null;

    /**
     * 블록 기본클래스를 정의한다.
     * 하위 블록 클래스의 경우 자유롭게 설정변수를 추가로 받을 수 있다.
     *
     * @param editor - 이 블록이 속한 에디터
     */
    constructor(public editor: Editor) {
        if (this.getKind() == 'atomic') {
            this.anchor = new Anchor(this, { name: 'anchor' });
        }
    }

    /**
     * 블록을 초기화한다.
     * 하위 블록 클래스가 재정의하여 자신이 사용할 툴을 추가하거나, 기타 초기화작업을 수행한다.
     * 최종적으로 에디터의 블록목록에 추가될 버튼 DOM 을 반환한다.
     *
     * @param editor - 블록이 추가되는 에디터
     * @param self - 하위 블록 클래스
     * @returns 블록추가 버튼 DOM
     */
    static init(editor: Editor, self: BlockClass | null = null): Dom {
        const $button = Html.create('button', { type: 'button', 'data-type': this.type });
        $button.append(Html.icon(this.icon));

        const $label = Html.create('span');
        $label.html(this.label);
        $button.append($label);

        if (self === null) {
            $button.on('mousedown', (_, e) => {
                e.preventDefault();
            });

            $button.on('click', async () => {
                await editor.insertBlock(this.create(editor));
            });
        }

        return $button;
    }

    /**
     * 현재 블록의 인스턴스를 생성한다.
     * 하위 블록 클래스가 재정의하여 자신의 기본 옵션값등을 추가하여 블록객체를 반환시킨다.
     *
     * @param editor - 새로 생성될 블록 클래스가 에디터
     * @param properties - 새로 생성될 블록에 전달할 프로퍼티
     * @returns 블록 클래스
     */
    static create(editor: Editor, properties: { [key: string]: any } = null): Block {
        const Self = this as unknown as new (editor: Editor, properties?: { [key: string]: any }) => Block;
        return new Self(editor, properties);
    }

    /**
     * 블록고유값을 지정한다. 블록 DOM 이 이미 생성되어 있다면 관련 속성도 함께 갱신한다.
     *
     * @param value - 지정할 블록고유값
     */
    setId(value: string): void {
        this.#id = value;

        if (this.$block !== null) {
            this.$block.setStyleProperty('--rootin-block', '--rootin-block-' + value);
            this.$block.setAttr('data-block', value);

            Html.all('rootin-element', this.$block).forEach(($el) => {
                const element = $el.getData('element') as Element | null;
                element?.$getElement().setAttr('data-element', element.getId());
            });
        }
    }

    /**
     * 블록고유값을 가져온다.
     * 현재 설정된 블록고유값이 없다면 UUID 를 이용해 신규로 생성한다.
     *
     * @returns 블록고유값
     */
    getId(): string {
        if (this.#id === null) {
            this.setId(UUID.v7());
        }

        return this.#id;
    }

    /**
     * 블록타입을 가져온다.
     *
     * @returns 블록타입
     */
    getType(): string {
        return (this.constructor as typeof Block).type;
    }

    /**
     * 블록종류를 가져온다.
     *
     * @returns 블록종류
     */
    getKind(): 'inline' | 'atomic' {
        return (this.constructor as typeof Block).kind;
    }

    /**
     * 드래그 중인 파일을 블록이 받을 수 있는지 판단한다.
     *
     * @param fileType - 파일의 MIME 타입
     * @returns 판단여부 (insert : 블록추가 및 파일드롭 허용, drop : 파일드롭만 허용, false : 파일드롭을 서용하지 않음)
     */
    static acceptFileDrop(fileType: string): 'insert' | 'drop' | false {
        return false;
    }

    /**
     * 블록 DOM을 가져온다
     *
     * @returns 블록 DOM
     */
    $getBlock(): Dom {
        if (this.$block === null) {
            const el = document.createElement('rootin-block') as RootinBlock;
            el.setBlock(this);

            this.$block = Html.el(el);
            this.$block.setStyleProperty('--rootin-block', '--rootin-block-' + this.getId());
            this.$block.setAttr('data-block', this.getId());
            this.$block.setAttr('data-type', this.getType());
            this.$block.setAttr('data-kind', this.getKind());
            this.$block.setAttr('contenteditable', 'false');
            this.$block.setData('block', this, false);

            if (this.anchor !== null) {
                this.$block.append(this.anchor.$getElement());
            }
        }

        return this.$block;
    }

    /**
     * 현재 블록을 담고 있는 부모 컨테이너 엘리먼트를 가져온다.
     *
     * @returns 현재 블록의 부모 컨테이너 엘리먼트
     */
    getParent(): Container {
        const $container = this.$getBlock().getParents('rootin-element[data-type="container"]');
        return ($container?.getData('element') as Container) ?? null;
    }

    /**
     * 현재 블록의 부모 중 특정 타입의 부모가 존재한다면 가져온다.
     *
     * @param type - 가져올 부모블록타입
     * @returns 부모블록
     */
    getParents(type: string): Block | null {
        if (this.getType() == type) {
            return this;
        }

        let parent = this.getParent()?.block ?? null;
        while ((parent?.getType() ?? 'root') !== 'root') {
            if (parent?.getType() == type) {
                return parent;
            }

            parent = parent.getParent()?.block ?? null;
        }

        return null;
    }

    /**
     * 타입 문자열로 블록 클래스를 가져온다.
     *
     * @param type - 블록타입
     * @returns 해당 블록 클래스
     */
    getBlockClass(type: string): BlockClass | null {
        return this.editor.getBlockClass(type);
    }

    /**
     * 같은 컨테이너 안에서 바로 이전/다음 형제 블록을 가져온다.
     *
     * @param direction - 'prev' | 'next'
     * @returns 형제 블록 (없으면 null)
     */
    getAdjacentBlock(direction: 'prev' | 'next'): Block | null {
        const $adjacent =
            direction === 'prev' ? this.$getBlock().prev('rootin-block') : this.$getBlock().next('rootin-block');
        return ($adjacent?.getData('block') as Block) ?? null;
    }

    /**
     * 블록에 포커스를 지정한다.
     *
     * @param to_start - 포커스 위치 (TRUE : 처음, FALSE : 끝, NULL : 앵커)
     * @param from - 포커스 진입점
     */
    focus(to_start: boolean | null, from: 'before' | 'after' | null = null): void {
        if (to_start === null && this.anchor !== null) {
            this.anchoredFrom = from;
            this.anchor.focus(false);
            return;
        }

        // anchor 가 이 블록의 평상시 캐럿정지점 역할까지 겸하면(alwaysEditable), 화살표키로
        // 블록에 진입할 때도 별도 필드 검색 없이 곧장 anchor 로 들어온다.
        if (this.anchor !== null && this.anchor.alwaysEditable === true) {
            this.anchor.focus(to_start);
            return;
        }

        const $elements = Html.all(
            'rootin-element:not([data-type="anchor"]):not([data-type="container"])',
            this.$getBlock()
        ).getList() as Dom<RootinElement>[];
        const $target = to_start == true ? $elements.at(0) : $elements.at(-1);

        if ($target !== undefined) {
            $target.getEl().element.focus(to_start == true);
            return;
        }

        this.anchor?.focus(to_start == true);
    }

    /**
     * 블록을 렌더링한다.
     */
    abstract render(): Promise<void>;

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    abstract isEmpty(): boolean;

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    abstract getValue(element?: Element): any;

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    abstract setValue(value: { [key: string]: any }, is_default_value?: boolean): Promise<void>;

    /**
     * 블록의 HTML 을 받아, 블록값을 지정한다.
     *
     * @param html - 블록 HTML
     */
    async setValueFromHtml(html: string): Promise<void> {}

    /**
     * 현재 블록내 선택영역이 존재할 경우, 선택영역을 텍스트로 추출하고 블록에서 제거한다.
     *
     * @param is_all - 선택영역 부분추출이 아닌 전체를 추출할지 여부
     * @returns 추출한 텍스트 목록과, 선택 이후 남은영역이 포함된 블록(없는 경우 NULL)
     */
    async extractSelection(is_all: boolean): Promise<{ texts: string[]; remain: Block | null }> {
        return { texts: [], remain: null };
    }

    /**
     * 블록이 삽입되었을 때 이벤트를 처리한다.
     */
    async onInserted(): Promise<void> {}

    /**
     * 블록이 가진 엘리먼트에서 편집이 일어난 이후 이벤트를 처리한다.
     *
     * @param element - 편집이 일어난 엘리먼트
     */
    onEdit(element: Element): void {}

    /**
     * 블록이 포커스를 받았을 때 이벤트를 처리한다.
     *
     * @param element - 캐럿이 위치한 엘리먼트 (NULL 이면 아직 캐럿 위치가 없다는 뜻)
     */
    onFocus(element: Element | null): void {}

    /**
     * 블록이 포커스를 받을 가능성이 있을 때 이벤트를 처리한다.
     */
    onFocusable(): void {}

    /**
     * 다중블록 선택이 시작/종료될 때 이벤트를 처리한다.
     *
     * @param is_select - 선택여부
     */
    onSelect(is_select: boolean): void {}

    /**
     * 블록이 포커스를 잃었을 때 이벤트를 처리한다.
     */
    onBlur(): void {}

    /**
     * 블록내에서 Enter 키입력이 발생했을 때 이벤트를 처리한다.
     *
     * @param element - 실제로 Enter 키입력이 발생한 엘리먼트 (NULL 이면 블록자체)
     */
    onEnter(element: Element | null): void {}

    /**
     * 클립보드에서 붙여넣기 하였을 때 이벤트를 처리한다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param focused - Element 인 경우 나의 블록의 포커스된 엘리먼트, `Block`인 경우 새 블록을 삽입할 기준블록
     * @returns 처리결과 (true 인 경우 모든 데이터를 처리함, 그렇지 않은 경우 처리되지 않은 데이터)
     */
    async onPaste(type: string, data: any, focused: Block | Element | null = null): Promise<true | any> {
        return data;
    }

    /**
     * 파일이 드롭되었을 때 이벤트를 처리한다.
     *
     * @param file - 드롭된 파일
     */
    async onFileDrop(file: File): Promise<void> {}

    /**
     * 현재 블록내에 캐럿이 이동될 때 이벤트를 처리한다.
     *
     * @param direction - 'up' | 'down' | 'left' | 'right' | 'forward' | 'backward'
     * @returns 브라우저 기본 캐릿위치 이동을 차단할지 여부
     */
    onCaretMove(direction: 'up' | 'down' | 'left' | 'right' | 'forward' | 'backward'): boolean {
        if (this.anchor === null || document.activeElement !== this.anchor.$getElement().getEl()) {
            return false;
        }

        const forward = direction === 'down' || direction === 'right' || direction === 'forward';
        const backward = direction === 'up' || direction === 'left' || direction === 'backward';
        if (forward === false && backward === false) {
            return false;
        }

        const entered = this.anchoredFrom;
        if (entered === null) {
            return false;
        }

        this.anchoredFrom = null;

        if (entered === 'after') {
            if (forward === true) {
                this.getAdjacentBlock('next')?.focus(true);
            } else {
                this.focus(false);
            }
        } else {
            if (forward === true) {
                this.focus(true);
            } else {
                this.getAdjacentBlock('prev')?.focus(false);
            }
        }

        return true;
    }

    /**
     * 이 블록이 DOM 에 연결되었을 때 이벤트를 처리한다.
     */
    async onConnect(): Promise<void> {
        this.editor.syncBlocks(this);
    }

    /**
     * 이 블록이 DOM 에서 연결해제되었을 때 이벤트를 처리한다.
     */
    async onDisconnect(): Promise<void> {
        this.editor.syncBlocks(this);
    }

    /**
     * 우클릭 메뉴가 노출될 때 이벤트를 처리한다.
     * 블록 스스로 필요한 별도 메뉴가 있다면 메뉴 아이템을 추가한다.
     *
     * @param menu - 메뉴
     * @param e - contextmenu 이벤트
     * @returns 더 이상 메뉴를 변경하지 않도록 차단할지 여부
     */
    onMenu(menu: Menu, e: MouseEvent): boolean {
        throw new Error(
            '구현 예정 — selection.get() 이 kind===range 이고 collapsed 아니며 e.target 이 그 ' +
                'Range 안이면 포커스 유지, 아니면 editor.focus.setBlock(this) 로 이동. 항목 기여 ' +
                '없이 항상 false 반환.'
        );
    }
}

/**
 * 블록 루트 DOM
 */
export class RootinBlock extends HTMLElement {
    block: Block = null;

    setBlock(block: Block): void {
        this.block = block;
    }

    connectedCallback(): void {
        this.block?.onConnect();
    }

    disconnectedCallback(): void {
        this.block?.onDisconnect();
    }
}
customElements.define('rootin-block', RootinBlock);
